import { useCallback, useMemo, useState } from 'react';

import { toast } from 'react-toastify';

import { useRoomContext } from '@src/contexts';
import SocketService from '@src/services/SocketService';

import { MAX_NUM_OF_OPTIONS } from './createPoll.constants';

import { TPollTime } from '@src/types';

type TVoteType = 'SINGLE' | 'MULTIPLE';

const useCreatePoll = () => {
  const [pollQuestion, setPollQuestion] = useState('');
  const [selectedPollTime, setSelectedPollTime] = useState<TPollTime>('10');
  const [selectedVoteType, setSelectedVoteType] = useState<TVoteType>('SINGLE');
  const [pollOptions, setPollOptions] = useState<string[]>(['']);

  const { currentPeerId } = useRoomContext();

  const isReachedMaxOptions = useMemo(() => pollOptions.length >= MAX_NUM_OF_OPTIONS, [pollOptions.length]);

  const isAllOptionsFilled = useMemo(
    () => !!pollOptions.length && !pollOptions.every((opt) => opt.trim().length > 0),
    [pollOptions]
  );

  const hasDuplicatedOptions = useMemo(
    () => !!pollOptions.filter((item, index) => pollOptions.indexOf(item) !== index).length,
    [pollOptions]
  );

  const hasLessThanTwoOptions = useMemo(() => pollOptions.length <= 1, [pollOptions]);

  const isAddButtonDisabled = useMemo(
    () => isAllOptionsFilled || isReachedMaxOptions,
    [isAllOptionsFilled, isReachedMaxOptions]
  );

  const isCreateButtonDisabled = useMemo(
    () =>
      hasLessThanTwoOptions ||
      isAllOptionsFilled ||
      !pollQuestion.trim().length ||
      !pollOptions.length ||
      hasDuplicatedOptions,
    [hasDuplicatedOptions, hasLessThanTwoOptions, isAllOptionsFilled, pollOptions.length, pollQuestion]
  );

  const addButtonTooltip = useMemo(
    () =>
      isReachedMaxOptions
        ? `You can add only ${MAX_NUM_OF_OPTIONS} `
        : isAddButtonDisabled
          ? 'Please fill the empty option'
          : 'Add option',
    [isAddButtonDisabled, isReachedMaxOptions]
  );

  const createButtonTooltip = useMemo(
    () =>
      hasLessThanTwoOptions
        ? 'Poll should have at least two options'
        : hasDuplicatedOptions
          ? 'The same option has been inserted before'
          : isCreateButtonDisabled
            ? 'Please fill the empty fields'
            : 'Create poll',
    [hasDuplicatedOptions, hasLessThanTwoOptions, isCreateButtonDisabled]
  );

  const handleChangeQuestion = useCallback((questionText: string) => {
    setPollQuestion(questionText);
  }, []);

  const handleAppendOption = useCallback(() => {
    setPollOptions((prevOptions) => [...prevOptions, '']);
  }, []);

  const handleRemoveOption = useCallback((index: number) => {
    setPollOptions((prevOptions) => prevOptions.filter((_, idx) => idx !== index));
  }, []);

  const handleChangeOptionText = useCallback((newText: string, index: number) => {
    setPollOptions((prevOptions) => {
      const clonedOptions = structuredClone(prevOptions);
      clonedOptions[index] = newText;
      return clonedOptions;
    });
  }, []);

  const handleResetStates = useCallback(() => {
    setPollOptions(['']);
    setPollQuestion('');
  }, []);

  const handleCreatePoll = useCallback(() => {
    SocketService.emit('createPoll', {
      question: pollQuestion,
      options: pollOptions,
      creatorId: currentPeerId,
      closingTime: selectedPollTime,
      voteType: selectedVoteType,
    });
    handleResetStates();
    toast.success('Poll created successfully!', {
      autoClose: 1000,
      position: 'top-left',
      className: 'toast toast-black',
    });
  }, [currentPeerId, handleResetStates, pollOptions, pollQuestion, selectedPollTime, selectedVoteType]);

  const handleChangeVoteType = useCallback((voteType: TVoteType) => {
    setSelectedVoteType(voteType);
  }, []);

  const handleSelectPollTime = useCallback((value: TPollTime) => {
    setSelectedPollTime(value);
  }, []);

  return {
    pollOptions,
    pollQuestion,
    addButtonTooltip,
    selectedPollTime,
    selectedVoteType,
    createButtonTooltip,
    isAddButtonDisabled,
    isCreateButtonDisabled,

    handleCreatePoll,
    handleAppendOption,
    handleRemoveOption,
    handleSelectPollTime,
    handleChangeQuestion,
    handleChangeVoteType,
    handleChangeOptionText,
  };
};

export default useCreatePoll;
